import * as React from 'react';

import requestCountry from 'js/lib/requestCountry';

import { Typography2 } from '@coursera/cds-core';

import {
  PRIVACY_NOTICE_ADDITIONAL_INFORMATION_SECTION_UNIQUE_ID,
  PRIVACY_NOTICE_KEY_INFORMATION_SECTION_UNIQUE_ID,
} from 'bundles/about/AboutConstants';
import type { MarketingContent, Maybe } from 'bundles/about/__generated__/baseContentfulTypes';
import Markdown from 'bundles/cms/components/Markdown';

import _t from 'i18n!nls/about';

type Props = {
  title: string;
  content: Array<Maybe<MarketingContent>>;
};

const TableRenderer = ({ children, title }: React.PropsWithChildren<{ title?: string }>) => (
  <div className="responsive-table-wrapper">
    <table>
      {title && (
        <Typography2 component="caption" variant="bodyPrimary">
          {title}
        </Typography2>
      )}
      {children}
    </table>
  </div>
);

class AboutSection extends React.Component<Props> {
  // Parameterized strings work with Markdown anchors in Contentful; this allows us to enable within-page linking
  parameterizeString = (markDownString: string) => {
    return markDownString
      ?.toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  renderSubsection = (subsection: $TSFixMe) => {
    if (!subsection) return null;
    const showFranceLocalizedPrivacyNoticeLanguage = requestCountry.get() === 'FR';
    const parameterizeTitle = this.parameterizeString(subsection.title);
    const parameterizeSectionName = this.parameterizeString(subsection.sectionName);
    const uniqueId = parameterizeSectionName ? parameterizeTitle + '-' + parameterizeSectionName : parameterizeTitle;

    if (parameterizeTitle === PRIVACY_NOTICE_KEY_INFORMATION_SECTION_UNIQUE_ID) {
      return (
        <div className="key-information" key={uniqueId} id={parameterizeTitle} data-testid={parameterizeTitle}>
          <Markdown linkTarget="_self" source={subsection.description} renderers={{ table: TableRenderer }} />
        </div>
      );
    }

    if (parameterizeTitle === 'collection-and-use-of-personal-information-u-s-state-privacy-laws-notice') {
      return (
        <div key={uniqueId} id={parameterizeTitle} data-testid={parameterizeTitle}>
          <Markdown
            linkTarget="_self"
            source={subsection.description}
            renderers={{
              table: (props) => (
                <TableRenderer title={_t('Personal Information Categories')}>{props.children}</TableRenderer>
              ),
            }}
          />
        </div>
      );
    }

    if (
      !showFranceLocalizedPrivacyNoticeLanguage &&
      parameterizeTitle === PRIVACY_NOTICE_ADDITIONAL_INFORMATION_SECTION_UNIQUE_ID
    ) {
      return null;
    }

    if (
      showFranceLocalizedPrivacyNoticeLanguage &&
      parameterizeTitle === PRIVACY_NOTICE_ADDITIONAL_INFORMATION_SECTION_UNIQUE_ID
    ) {
      return (
        <div className="additional-information" key={uniqueId} id={parameterizeTitle} data-testid={parameterizeTitle}>
          <Markdown linkTarget="_self" source={subsection.description} renderers={{ table: TableRenderer }} />
        </div>
      );
    }

    return (
      <div key={uniqueId} id={parameterizeTitle} data-testid={parameterizeTitle}>
        <Markdown linkTarget="_self" source={subsection.description} renderers={{ table: TableRenderer }} />
      </div>
    );
  };

  render() {
    const { title, content } = this.props;
    return (
      <div className="rc-AboutSection" id={this.parameterizeString(title)}>
        <hr className="header-after" />
        <h1>{title}</h1>
        <hr className="header-after" />
        {content.map(this.renderSubsection)}
      </div>
    );
  }
}

export default AboutSection;
